import { useAppForm } from '@massilia/features'
import { useImportBookingMutation } from '@massilia/queries'
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'

import { resolveAuthErrorMessage } from '@/lib/auth-error-message'
import { importReservationSchema } from '@/lib/schemas'

interface ImportReservationDialogProps {
  isOpen: boolean
  onClose: () => void
}

export function ImportReservationDialog({ isOpen, onClose }: ImportReservationDialogProps) {
  const [submitStatus, setSubmitStatus] = useState<'error' | 'idle' | 'success'>('idle')
  const [submitError, setSubmitError] = useState<null | string>(null)
  const { mutateAsync: importBooking, reset: resetMutation } = useImportBookingMutation()

  const form = useAppForm({
    defaultValues: {
      email: '',
      lastName: '',
      dossierNumber: '',
    },
    listeners: {
      onChange: () => {
        if (submitError) {
          setSubmitError(null)
          setSubmitStatus('idle')
        }
      },
    },
    onSubmit: async ({ value }) => {
      try {
        await importBooking(value)
        setSubmitStatus('success')
        setTimeout(() => {
          onClose()
        }, 1500)
      } catch (error) {
        setSubmitStatus('error')
        setSubmitError(resolveAuthErrorMessage(error))
      }
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'blur',
    }),
    validators: {
      onDynamic: ({ value }) =>
        standardSchemaValidators.validate(
          { validationSource: 'form', value },
          importReservationSchema
        ),
    },
  })

  // Reset everything when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay to avoid visual glitches during close animation
      const timeout = setTimeout(() => {
        form.reset()
        resetMutation()
        setSubmitStatus('idle')
        setSubmitError(null)
      }, 200)
      return () => clearTimeout(timeout)
    }
  }, [isOpen, form, resetMutation])

  return (
    <Dialog onOpenChange={(open) => !open && onClose()} open={isOpen}>
      <DialogContent className="gap-6 rounded-2xl bg-white p-8">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            Importer une réservation
          </DialogTitle>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit()
          }}
        >
          <form.AppField
            name="dossierNumber"
            children={(field) => (
              <field.TextField label="Numéro de dossier" placeholder="ex: MVS23832" />
            )}
          />
          <form.AppField
            name="email"
            children={(field) => (
              <field.TextField
                label="Adresse email utilisée lors de la réservation"
                placeholder="cldourlen@laposte.net"
              />
            )}
          />
          <form.AppField
            name="lastName"
            children={(field) => (
              <field.TextField label="Nom de famille (d'un des passagers)" placeholder="DOURLENS" />
            )}
          />

          {submitError && <p className="text-sm font-medium text-red-600">{submitError}</p>}

          <form.Subscribe selector={(state) => state.isSubmitting}>
            {(isSubmitting) => (
              <Button
                className={`w-full px-4 py-3 text-base font-medium text-white transition-all duration-300 ${
                  submitStatus === 'success'
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-brand-600 hover:bg-brand-700'
                }`}
                disabled={isSubmitting || submitStatus === 'success'}
                type="submit"
              >
                {submitStatus === 'success' ? (
                  <Check className="size-5" />
                ) : isSubmitting ? (
                  'Importation...'
                ) : (
                  'Ajouter'
                )}
              </Button>
            )}
          </form.Subscribe>
        </form>
      </DialogContent>
    </Dialog>
  )
}
